import { format } from "date-fns";
import { Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { logClientEvent } from "./AuditLog";

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const calcAge = (dob) => {
  if (!dob) return null;
  const age = new Date(Date.now() - new Date(dob).getTime()).getUTCFullYear() - 1970;
  return Number.isFinite(age) && age >= 0 && age < 130 ? age : null;
};

export const printIvBagLabel = ({ appointment, patient, service, addOns = [], staffNames = [], preparedBy }) => {
  if (!appointment || !patient) { toast.error('Patient details not loaded yet'); return; }
  const start = appointment.start_datetime ? new Date(appointment.start_datetime) : null;
  const age = calcAge(patient.date_of_birth);
  const dob = patient.date_of_birth ? format(new Date(patient.date_of_birth + 'T00:00:00'), 'MM/dd/yyyy') : '—';
  const addOnText = addOns.filter(Boolean).join(' · ');
  const notes = (appointment.notes || '').trim();

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>IV Label – ${esc(patient.first_name)} ${esc(patient.last_name)}</title>
<style>
  @page { size: 4in 2in; margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  body { font-family: 'Helvetica Neue', Arial, sans-serif; color: #111; }
  .label { width: 4in; height: 2in; padding: 0.12in 0.16in; display: flex; flex-direction: column; border: 1px dashed #ccc; page-break-after: always; }
  .top { display: flex; justify-content: space-between; align-items: baseline; border-bottom: 1.5px solid #b8860b; padding-bottom: 2px; }
  .brand { font-size: 9pt; font-weight: 700; letter-spacing: 1.5px; }
  .brand .g { color: #b8860b; } .brand .s { color: #777; }
  .when { font-size: 8pt; color: #333; font-weight: 600; }
  .name { font-size: 17pt; font-weight: 800; margin-top: 4px; line-height: 1.05; letter-spacing: 0.2px; }
  .meta { font-size: 8.5pt; color: #333; margin-top: 2px; }
  .svc { font-size: 11pt; font-weight: 700; margin-top: 5px; color: #111; }
  .add { font-size: 8.5pt; color: #222; margin-top: 2px; }
  .add b { color: #b8860b; text-transform: uppercase; letter-spacing: 0.5px; font-size: 7pt; }
  .allergy { font-size: 8pt; font-weight: 700; color: #c0392b; margin-top: 3px; }
  .foot { margin-top: auto; display: flex; justify-content: space-between; font-size: 7pt; color: #666; border-top: 1px solid #ddd; padding-top: 2px; }
  @media screen { body { padding: 20px; background: #f3f3f3; } .label { background: #fff; box-shadow: 0 2px 8px rgba(0,0,0,.15); } }
</style></head><body>
<div class="label">
  <div class="top">
    <div class="brand"><span class="g">ELITE</span><span class="s">IV</span></div>
    <div class="when">${start ? esc(format(start, 'EEE MMM d, yyyy · h:mm a')) : '—'}</div>
  </div>
  <div class="name">${esc(patient.first_name)} ${esc(patient.last_name)}</div>
  <div class="meta">DOB ${esc(dob)}${age != null ? ` (${age} y)` : ''}${patient.gender ? ` · ${esc(patient.gender)}` : ''}</div>
  <div class="svc">${esc(service?.name || 'IV Therapy')}</div>
  ${addOnText ? `<div class="add"><b>Add-ons</b> ${esc(addOnText)}</div>` : ''}
  ${notes ? `<div class="add"><b>Notes</b> ${esc(notes.slice(0, 90))}${notes.length > 90 ? '…' : ''}</div>` : ''}
  ${patient.allergies ? `<div class="allergy">⚠ ALLERGY: ${esc(patient.allergies)}</div>` : ''}
  <div class="foot">
    <span>RN: ${esc(staffNames.join(', ') || '—')}</span>
    <span>Printed ${esc(format(new Date(), 'M/d h:mm a'))}${preparedBy ? ` · ${esc(preparedBy)}` : ''}</span>
  </div>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 300); };</script>
</body></html>`;

  const w = window.open('', '_blank', 'width=520,height=360');
  if (!w) { toast.error('Pop-up blocked. Please allow pop-ups to print.'); return; }
  logClientEvent('Printed IV bag label', { patient_id: patient.id, resource_type: 'appointment', resource_id: appointment.id });
  w.document.open(); w.document.write(html); w.document.close();
};

export const PrintLabelButton = ({ onClick, testId, className = '', size = 'sm', label = 'Print Label' }) => (
  <Button type="button" size={size} variant="outline" onClick={(e) => { e.stopPropagation(); onClick(); }} data-testid={testId}
    className={`border-primary/40 text-primary hover:bg-primary/10 text-xs h-7 ${className}`}>
    <Tag className="w-3 h-3 mr-1" />{label}
  </Button>
);
